<template>
  <div id="certification-edit-page" class="certification-edit-page">
    <div class="certification-edit-page__header">
      <div class="certification-edit-page__title">
        <h2>{{ certificationTitle }}</h2>
        <span
          v-if="showParentCertificationStatus"
          class="certification-edit-page__status-indicator"
        >
          Certification in {{ parentCertificationStatusLabel }}
        </span>
        <vs-button
          v-if="isParentCertificationDraft && isAdminUser"
          class="certification-edit-page__publish-certification"
          size="small"
          :disabled="isPublishingCertification"
          @click="publishCertification"
        >
          Publish certification
        </vs-button>
      </div>
    </div>

    <div class="certification-edit-page__tabs">
      <div class="certification-edit-page__tab-bar">
        <button
          v-for="tab in visibleTabs"
          :key="tab.key"
          class="certification-edit-page__tab-button"
          :class="{
            'certification-edit-page__tab-button--active':
              activeTab === tab.key,
          }"
          type="button"
          @click="requestTabChange(tab.key)"
        >
          {{ tab.label }}
        </button>
      </div>

      <div class="certification-edit-page__tab-content">
        <div
          class="certification-general-tab-content"
          v-if="isCreationMode || activeTab === 'general'"
          v-show="activeTab === 'general'"
        >
          <certification-form
            ref="certificationForm"
            :certificationVersion="currentCertificationVersion"
            :certificationExam="currentCertificationExam"
            :isCreationMode="isCreationMode"
            :isCreatingCertification="isCreatingCertification"
            :isLoading="isLoading"
            :requestChangeNote="requestChangeNote"
            :pendingBadgeUpload="pendingCreationBadgeUpload"
            :isParentCertificationDraft="isParentCertificationDraft"
            :creationCreatedAt="creationStartedAt"
            :creationCreatedByName="creationCreatedByName"
            @certification-updated="refreshCertificationVersion"
            @pending-badge-consumed="clearPendingCreationBadgeUpload"
            @save-creation="saveCreationAsDraft"
          />
        </div>
        <div
          class="certification-exam-tab-content"
          v-if="activeTab === 'exam' && isExamFormReady"
          v-show="activeTab === 'exam'"
        >
          <exam-form
            ref="examForm"
            :exam="currentCertificationExam"
            :ensureExamExists="ensureExamExists"
            :requestChangeNote="requestChangeNote"
            :isParentCertificationDraft="isParentCertificationDraft"
          />
        </div>
        <launch-configuration-form
          v-if="activeTab === 'launch' && currentCertificationVersion"
          ref="launchConfigurationForm"
          :certificationVersion="currentCertificationVersion"
        />
      </div>
    </div>
    <change-notes-popup v-if="certificationChangeNotesEnabled" ref="changeNotesPopup" />
    <unsaved-changes-popup
      :active="showUnsavedWarning"
      primary-label="Stay to save"
      @close="cancelTabChange"
      @save="cancelTabChange"
      @discard="discardAndNavigate"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import CONSTANTS from '@/constants'
import { ROLE_ADMIN, ROLE_CERTIFICATION_MANAGER } from '@/constants/roles'
import ChangeNotesPopup from '@/components/ChangeNotesPopup.vue'
import UnsavedChangesPopup from '@/components/UnsavedChangesPopup.vue'
import CertificationForm from './forms/certifications/CertificationForm.vue'
import ExamForm from './forms/certifications/ExamForm.vue'
import LaunchConfigurationForm from './forms/certifications/LaunchConfigurationForm.vue'
import { humanizeStatus } from '@/utils/statusHelper'

export default {
  name: 'certification-edit-page',
  components: {
    ChangeNotesPopup,
    UnsavedChangesPopup,
    CertificationForm,
    ExamForm,
    LaunchConfigurationForm,
  },
  data() {
    return {
      activeTab: 'general',
      isPublishingCertification: false,
      isLoading: false,
      isCreatingCertification: false,
      isExamFormReady: false,
      pendingCreationBadgeUpload: null,
      creationStartedAt: '',
      pendingTab: null,
      showUnsavedWarning: false,
      pendingTabFromRoute: false,
      pendingLeaveRoute: null,
      pendingLeaveNext: null,
      allowSuccessfulCreationRedirect: null,
      tabs: [
        { key: 'general', label: 'General Info' },
        { key: 'exam', label: 'Exam Configuration' },
        { key: 'launch', label: 'Launch Configuration' },
      ],
    }
  },
  computed: {
    ...mapGetters('featureFlags', ['featureFlagsMap']),
    ...mapGetters('certification', [
      'getCertificationVersion',
      'getCertificationExam',
    ]),
    ...mapGetters('users', ['currentUserRoles', 'getCurrentUser']),
    certificationVersionId() {
      return this.$route.params.certificationVersionId
    },
    isCreationMode() {
      return this.$route.meta && this.$route.meta.isCreation === true
    },
    isCreationFormMode() {
      // Creation may hydrate before route replacement; only permit the initial
      // orchestration while there is no persisted Version.
      return this.isCreationMode && !this.getCertificationVersion
    },
    isAdminUser() {
      return (this.currentUserRoles || []).includes(ROLE_ADMIN)
    },
    canAccessLaunchConfiguration() {
      return (
        this.isAdminUser ||
        (this.currentUserRoles || []).includes(ROLE_CERTIFICATION_MANAGER)
      )
    },
    visibleTabs() {
      if (this.isCreationMode) {
        return this.tabs.filter((tab) => tab.key === 'general')
      }

      return this.tabs.filter(
        (tab) => tab.key !== 'launch' || this.canAccessLaunchConfiguration
      )
    },
    canReuseActiveEditContext() {
      return this.canReuseEditContext(this.getEditRouteContext())
    },
    selectedExamId() {
      return this.$route.query.examId || null
    },
    certificationTitle() {
      if (this.isCreationMode) return 'New certification — v1.0'
      if (!this.currentCertificationVersion) return 'Certification'

      const name =
        this.currentCertificationVersion.certification_short_name ||
        this.currentCertificationVersion.name ||
        'Certification'
      const certMajor = this.currentCertificationVersion.major_version
      const examMinor =
        this.currentCertificationExam &&
        this.currentCertificationExam.minor_version
      const version =
        certMajor && examMinor !== undefined && examMinor !== null
          ? `v${certMajor}.${examMinor}`
          : this.currentCertificationVersion.version_label ||
            this.currentCertificationVersion.major_version ||
            ''

      return version ? `${name} — ${version}` : name
    },
    currentCertificationVersion() {
      return this.getCertificationVersion || null
    },
    certificationChangeNotesEnabled() {
      return Boolean(
        this.featureFlagsMap && this.featureFlagsMap.certificationChangeNotes
      )
    },
    currentCertificationExam() {
      return this.getCertificationExam || null
    },
    parentCertificationStatus() {
      if (
        this.isCreationMode ||
        !this.currentCertificationVersion ||
        !this.currentCertificationVersion.certification
      ) {
        return null
      }

      return this.currentCertificationVersion.certification.status || null
    },
    parentCertificationStatusLabel() {
      return humanizeStatus(this.parentCertificationStatus)
    },
    showParentCertificationStatus() {
      return (
        this.parentCertificationStatus &&
        this.parentCertificationStatus !==
          CONSTANTS.CERTIFICATION_STATUSES.PUBLISHED
      )
    },
    isParentCertificationDraft() {
      return (
        this.parentCertificationStatus ===
        CONSTANTS.CERTIFICATION_STATUSES.DRAFT
      )
    },
    creationCreatedByName() {
      const currentUser = this.getCurrentUser || {}
      const profile = currentUser.profile && currentUser.profile.data
      const firstName = profile && profile.first_name
      const lastName = profile && profile.last_name
      const displayName = [firstName, lastName].filter(Boolean).join(' ')

      return displayName || currentUser.username || ''
    },
  },
  watch: {
    activeTab() {
      this.scrollPageTop()
    },
    '$route.query.tab': {
      immediate: true,
      handler(tab) {
        if (!this.visibleTabs.some((item) => item.key === tab)) return

        // On the immediate run no form is mounted yet, so there is nothing
        // that could be dirty: activate the tab without the guard.
        if (!this.visibleTabs.some((item) => this.formRefForTab(item.key))) {
          this.activeTab = tab
          return
        }

        this.pendingTabFromRoute = true
        this.requestTabChange(tab)
      },
    },
  },
  async created() {
    if (this.isCreationMode) {
      this.initializeCreationTimestamp()
      await this.resetCertificationEditContextAction()
      this.activeTab = 'general'
      return
    }
    if (this.canReuseActiveEditContext) {
      this.isExamFormReady = true
      return
    }
    await this.loadCertificationVersion()
  },
  async beforeRouteUpdate(to, from, next) {
    if (this.handleCreationRouteLeave(to, next)) return

    if (to.meta && to.meta.isCreation) {
      if (!from.meta || !from.meta.isCreation) {
        this.initializeCreationTimestamp()
      }
      await this.resetCertificationEditContextAction()
      this.activeTab = 'general'
      this.clearPendingCreationBadgeUpload()
      next()
      return
    }

    const targetContext = this.getEditRouteContext(to)
    if (this.canReuseEditContext(targetContext)) {
      this.isExamFormReady = true
    } else {
      await this.loadCertificationVersion(targetContext)
    }
    if (
      this.pendingCreationBadgeUpload &&
      this.pendingCreationBadgeUpload.certificationId !==
        targetContext.certificationId
    ) {
      this.clearPendingCreationBadgeUpload()
    }
    next()
  },
  mounted() {
    this.scrollPageTop()
  },
  methods: {
    ...mapActions('certification', [
      'getCertificationVersionWithCertificationAction',
      'getCertificationExamAction',
      'getExamByCertificationVersionAction',
      'createCertificationOrchestratorAction',
      'resetCertificationEditContextAction',
      'updateCertificationAction',
    ]),
    async publishCertification() {
      this.isPublishingCertification = true
      try {
        let changeNote
        if (this.certificationChangeNotesEnabled) {
          changeNote = await this.requestChangeNote()
          if (!changeNote) return
        }

        await this.updateCertificationAction({
          certificationId: this.currentCertificationVersion.certification.id,
          payload: {
            status: CONSTANTS.CERTIFICATION_STATUSES.PUBLISHED,
            ...(changeNote ? { change_note: changeNote } : {}),
          },
        })
        await this.refreshCertificationVersion()
        const certificationForm = this.$refs.certificationForm
        if (
          this.certificationChangeNotesEnabled &&
          certificationForm &&
          typeof certificationForm.refreshChangeNotes === 'function'
        ) {
          certificationForm.refreshChangeNotes()
        }
        this.$vs.notify({
          title: 'Success',
          text: 'Certification published',
          color: 'success',
        })
      } catch (error) {
        this.showError(error)
      } finally {
        this.isPublishingCertification = false
      }
    },
    initializeCreationTimestamp() {
      this.creationStartedAt = new Date().toISOString()
    },
    getEditRouteContext(route = this.$route) {
      const params = (route && route.params) || {}
      const query = (route && route.query) || {}
      return {
        certificationId: params.certificationId,
        certificationVersionId: params.certificationVersionId,
        selectedExamId: query.examId || null,
      }
    },
    canReuseEditContext({
      certificationId,
      certificationVersionId,
      selectedExamId,
    }) {
      const certificationVersion = this.getCertificationVersion || null
      const certificationExam = this.getCertificationExam || null
      const certification = certificationVersion
        ? certificationVersion.certification
        : null

      return Boolean(
        certificationVersion &&
          certificationVersion.id === certificationVersionId &&
          certification &&
          certification.id === certificationId &&
          certificationExam &&
          certificationExam.certification_version_id ===
            certificationVersionId &&
          (!selectedExamId || certificationExam.id === selectedExamId)
      )
    },
    async loadCertificationVersion({
      certificationVersionId = this.certificationVersionId,
      selectedExamId = this.selectedExamId,
    } = {}) {
      try {
        this.isLoading = true
        this.isExamFormReady = false
        this.$nextTick(() => {
          this.$vs.loading({
            container: '#certification-edit-page',
            scale: 0.6,
          })
        })
        await this.getCertificationVersionWithCertificationAction({
          certificationVersionId,
        })
        const hasSelectedExamLoaded =
          selectedExamId &&
          this.currentCertificationExam &&
          this.currentCertificationExam.id === selectedExamId
        if (!hasSelectedExamLoaded) {
          const examResponse = selectedExamId
            ? this.getCertificationExamAction({
                examId: selectedExamId,
              })
            : this.getExamByCertificationVersionAction({
                certificationVersionId,
              })
          await examResponse
        }
        this.isExamFormReady = true
      } catch (error) {
        if (error && error.response && error.response.status === 403) {
          this.$router.push({ name: 'page-not-authorized' })
          return
        }
        this.showError(error)
      } finally {
        this.isLoading = false
        this.$vs.loading.close('#certification-edit-page > .con-vs-loading')
      }
    },
    async refreshCertificationVersion() {
      await this.getCertificationVersionWithCertificationAction({
        certificationVersionId: this.certificationVersionId,
      })
    },
    async ensureExamExists() {
      if (this.currentCertificationExam && this.currentCertificationExam.id) {
        return this.currentCertificationExam.id
      }
      throw new Error('No exam found for this certification version')
    },
    async requestChangeNote() {
      return this.$refs.changeNotesPopup.open()
    },
    buildCreationPayload() {
      const certificationForm = this.$refs.certificationForm

      return {
        certification:
          certificationForm &&
          typeof certificationForm.buildCreationCertificationPayload ===
            'function'
            ? certificationForm.buildCreationCertificationPayload()
            : { name: '', short_name: '' },
        version:
          certificationForm &&
          typeof certificationForm.buildCreationVersionPayload === 'function'
            ? certificationForm.buildCreationVersionPayload()
            : {},
        exam: {},
      }
    },
    mapCreationIdentityValidationErrors(error, certificationForm) {
      const response = error && error.response
      const certificationErrors =
        response &&
        response.status === 400 &&
        response.data &&
        response.data.certification
      if (
        !certificationErrors ||
        !certificationForm ||
        typeof certificationForm.setCertificationIdentityErrors !== 'function'
      )
        return false

      const mappedErrors = {}
      if (
        Array.isArray(certificationErrors.name) &&
        certificationErrors.name.length
      ) {
        mappedErrors.certificationName = certificationErrors.name[0]
      }
      if (
        Array.isArray(certificationErrors.short_name) &&
        certificationErrors.short_name.length
      ) {
        mappedErrors.certificationShortName = certificationErrors.short_name[0]
      }
      if (!Object.keys(mappedErrors).length) return false

      certificationForm.setCertificationIdentityErrors(mappedErrors)
      return true
    },
    async saveCreationAsDraft() {
      if (!this.isCreationFormMode || this.isCreatingCertification) return

      const certificationForm = this.$refs.certificationForm
      if (!certificationForm) return
      const selectedBadgeFile = certificationForm.selectedBadgeFile

      const isIdentityValid = certificationForm.validateCreationIdentityFields()
      const hasValidVoucherFields =
        certificationForm.validateNumericVoucherFields()
      if (!isIdentityValid || !hasValidVoucherFields) return

      const payload = this.buildCreationPayload()
      this.isCreatingCertification = true
      try {
        let response
        try {
          response = await this.createCertificationOrchestratorAction(payload)
        } catch (error) {
          const hasMappedIdentityErrors =
            this.mapCreationIdentityValidationErrors(error, certificationForm)
          if (!hasMappedIdentityErrors) this.showError(error)
          throw error
        }

        if (
          typeof certificationForm.setCertificationIdentityErrors === 'function'
        ) {
          certificationForm.setCertificationIdentityErrors({})
        }

        const certificationId =
          response.certification && response.certification.id
        const certificationVersionId = response.version && response.version.id

        if (certificationId && certificationVersionId) {
          try {
            await this.getCertificationVersionWithCertificationAction({
              certificationVersionId,
            })
          } catch (hydrationError) {
            this.showError(hydrationError)
          }

          this.isExamFormReady = Boolean(this.getCertificationExam)

          if (selectedBadgeFile) {
            this.pendingCreationBadgeUpload = {
              certificationId,
              file: selectedBadgeFile,
            }
          }

          const redirect = {
            name: 'certification-version-edit',
            params: {
              certificationId,
              certificationVersionId,
            },
          }
          this.allowSuccessfulCreationRedirect = redirect
          try {
            await this.$router.replace(redirect)
          } catch (error) {
            this.allowSuccessfulCreationRedirect = null
            throw error
          }
        }
        return response
      } finally {
        this.isCreatingCertification = false
      }
    },
    clearPendingCreationBadgeUpload() {
      this.pendingCreationBadgeUpload = null
    },
    formRefForTab(tabKey) {
      const formRefsByTab = {
        general: this.$refs.certificationForm,
        exam: this.$refs.examForm,
        launch: this.$refs.launchConfigurationForm,
      }
      return formRefsByTab[tabKey] || null
    },
    hasDirtyCreationChanges() {
      const form = this.$refs.certificationForm
      return form && typeof form.isDirty === 'function' && form.isDirty()
    },
    isSuccessfulCreationRedirect(to) {
      return this.isExpectedCreationRoute(
        to,
        this.allowSuccessfulCreationRedirect
      )
    },
    isExpectedCreationRoute(to, target) {
      const toParams = (to && to.params) || {}
      const targetParams = (target && target.params) || {}
      return Boolean(
        target &&
          to &&
          to.name === target.name &&
          toParams.certificationId === targetParams.certificationId &&
          toParams.certificationVersionId ===
            targetParams.certificationVersionId
      )
    },
    handleCreationRouteLeave(to, next) {
      if (!this.isCreationMode || (to.meta && to.meta.isCreation)) return false

      if (this.isSuccessfulCreationRedirect(to)) {
        this.allowSuccessfulCreationRedirect = null
        this.showUnsavedWarning = false
        next()
        return true
      }

      if (!this.hasDirtyCreationChanges()) return false

      this.pendingLeaveRoute = to
      this.pendingLeaveNext = next
      this.showUnsavedWarning = true
      return true
    },
    requestTabChange(tabKey) {
      if (!this.visibleTabs.some((tab) => tab.key === tabKey)) return

      if (tabKey === this.activeTab) {
        this.pendingTabFromRoute = false
        return
      }
      const activeFormRef = this.formRefForTab(this.activeTab)
      const isDirty =
        activeFormRef && typeof activeFormRef.isDirty === 'function'
          ? activeFormRef.isDirty()
          : false
      if (isDirty) {
        this.pendingTab = tabKey
        this.showUnsavedWarning = true
        return
      }
      this.pendingTabFromRoute = false
      this.activeTab = tabKey
    },
    discardAndNavigate() {
      if (this.pendingLeaveRoute) {
        this.pendingLeaveRoute = null
        const next = this.pendingLeaveNext
        this.pendingLeaveNext = null
        this.showUnsavedWarning = false
        next()
        return
      }
      this.activeTab = this.pendingTab
      this.pendingTab = null
      this.pendingTabFromRoute = false
      this.showUnsavedWarning = false
    },
    cancelTabChange() {
      if (this.pendingLeaveRoute) {
        this.pendingLeaveRoute = null
        const next = this.pendingLeaveNext
        this.pendingLeaveNext = null
        this.showUnsavedWarning = false
        next(false)
        return
      }
      if (this.pendingTabFromRoute) {
        const query = { ...this.$route.query }
        if (this.activeTab === 'general') {
          delete query.tab
        } else {
          query.tab = this.activeTab
        }
        this.$router.replace({ query })
      }
      this.pendingTab = null
      this.pendingTabFromRoute = false
      this.showUnsavedWarning = false
    },
    scrollPageTop() {
      this.$nextTick(() => {
        if (typeof window.scrollTo !== 'function') return
        window.scrollTo(0, 0)
      })
    },
  },
  beforeRouteLeave(to, from, next) {
    if (this.handleCreationRouteLeave(to, next)) return
    next()
  },
  beforeDestroy() {
    this.$vs.loading.close('#certification-edit-page > .con-vs-loading')
  },
}
</script>

<style scoped>
.certification-edit-page {
  padding: 20px;
}

.certification-edit-page__header {
  align-items: flex-end;
  display: flex;
  justify-content: space-between;
  margin-bottom: 1.5rem;
}

.certification-edit-page__header h2 {
  margin: 0;
}

.certification-edit-page__title {
  align-items: center;
  display: flex;
  gap: 0.75rem;
}

.certification-edit-page__status-indicator {
  background: rgba(var(--vs-primary), 0.15);
  border-radius: 0.25rem;
  color: rgba(var(--vs-primary), 1);
  font-size: 0.875rem;
  padding: 0.25rem 0.5rem;
}

.certification-edit-page__tabs {
  width: 100%;
}

.certification-edit-page__tab-bar {
  align-items: flex-end;
  border-bottom: 1px solid #dae1e7;
  display: flex;
}

.certification-edit-page__tab-button {
  background: transparent;
  border: 0;
  border-bottom: 2px solid transparent;
  color: #626262;
  cursor: pointer;
  font-family: inherit;
  font-size: 1rem;
  font-weight: 600;
  margin: 0 1rem 0 0;
  min-height: 48px;
  padding: 0 1.5rem 1rem;
}

.certification-edit-page__tab-button--active {
  border-bottom-color: rgba(var(--vs-primary), 1);
  color: rgba(var(--vs-primary), 1);
}

.certification-edit-page__tab-content {
  padding-top: 1.75rem;
}

@media (max-width: 900px) {
  .certification-edit-page__header {
    align-items: stretch;
    flex-direction: column;
    gap: 1rem;
  }

  .certification-edit-page__title {
    align-items: center;
    gap: 2.25rem;
    justify-content: space-between;
  }
}
</style>
